/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/explicit-function-return-type */
import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { useAuth } from '../../hooks/auth';
import { useToast } from '../../hooks/toast';
import { FiCheckSquare } from 'react-icons/fi';
import { SubmitHandler, useForm } from 'react-hook-form';
import { CircularProgress, useTheme } from '@mui/material';
import moment from 'moment';

// Components
import Select from '../../components/Material/Select';
import { MuiCheckbox } from '../../components/Material/Checkbox';
import CustomLoadingButton from '../../components/Material/CustomLoadingButton';
import { EtanolChecklistReport } from '../../components/Reports/EtanolChecklistReport';
import { MuiInput } from '../../components/Material/MuiInput';
import { MuiDatePicker } from '../../components/Material/MuiDatePicker';
import { ChecklistImageUploader } from '../../components/ChecklistImages/ChecklistImageUploader';

// Constants
import { TipoCarreta, TipoCarretaValueEnum } from '../../constants/veiculos';
import { ChecklistDataEtanol } from '../../constants/checklist-etanol';
import { Printers } from '../../constants/printers';

// Utils
import { shouldApproveEtanolChecklist } from '../../utils/shouldApproveChecklist';
import {
  toChecklistReportImages,
  waitForImagesToLoad,
} from '../../utils/checklistImages';

// Services
import api from '../../services/api';

// Types
import {
  FormDataEtanol,
  IAgendamentoEtanolChecklist,
} from '../../types/checklist';
import type { ChecklistImage } from '../../types/checklistImages';

// Styles
import {
  ChecklistWrapper,
  Container,
  ContentWrapper,
  GridContainer,
  SectionTitle,
  SectionWrapper,
} from './styles';

// Assets
import sign331170 from '../../assets/icons/331170.jpg';
import sign301201 from '../../assets/icons/301201.png';
import liquidoInflamavel from '../../assets/icons/liquidoinflamavel3.jpg';
import { useChecklistImages } from '../../hooks/useChecklistImages';

interface IChecklistEtanolProps {
  isValid?: boolean;
}

enum TipoProdutoEnum {
  'Hidratado' = 'Hidratado', // 1
  'Etanol - Outros Fins' = 'Etanol - Outros Fins', // 2
  'Bagaço' = 'Bagaço', // 3
  'VHP' = 'VHP', // 4
  'Anidro' = 'Anidro', // 5
  'Hidratado Consumo' = 'Hidratado Consumo', // 6
  'Fertilizante' = 'Fertilizante', // 7
  'Oleo Fúsel' = 'Oleo Fúsel', // 8
  'Anidro Outros Fins' = 'Anidro Outros Fins', // 9
  'Anidro Sem Corante' = 'Anidro Sem Corante', // 10
}

const ChecklistEtanol = React.forwardRef<
  HTMLInputElement,
  IChecklistEtanolProps
>(({ isValid }, ref) => {
  const { addToast } = useToast();
  const navigate = useNavigate();
  const { signOut } = useAuth();
  const theme = useTheme();
  const location = useLocation();

  const params = location.state.params;

  const [loading, setLoading] = useState(false);
  const [loadingPost, setLoadingPost] = useState(false);
  const [checklist, setChecklist] = useState<any>();
  const [reportData, setReportData] = useState<any>(null);

  const [selectedImage, setSelectedImage] = useState<ChecklistImage | null>(
    null,
  );

  const { images, setImages, clearImages } = useChecklistImages();

  const imagesRef = useRef<ChecklistImage[]>([]);

  const minWidthMuiInput = '400px';
  const maxWidthMuiInput = '400px';

  const getTipoProduto = (produto: number) => {
    switch (produto) {
      case 1:
        return TipoProdutoEnum.Hidratado;
      case 2:
        return TipoProdutoEnum['Etanol - Outros Fins'];
      case 3:
        return TipoProdutoEnum.Bagaço;
      case 4:
        return TipoProdutoEnum.VHP;
      case 5:
        return TipoProdutoEnum.Anidro;
      case 6:
        return TipoProdutoEnum['Hidratado Consumo'];
      case 7:
        return TipoProdutoEnum.Fertilizante;
      case 8:
        return TipoProdutoEnum['Oleo Fúsel'];
      case 9:
        return TipoProdutoEnum['Anidro Outros Fins'];
      case 10:
        return TipoProdutoEnum['Anidro Sem Corante'];
      default:
        break;
    }
  };

  const paramsToAgendamentoData = {
    agendamentoid: params.agendamentoid,
    spot: params.spot,
    data: params.data,
    produto: params.produto,
    pedido: params.pedido,
    motoristaid: params.agendamentoData.motoristaid,
    cpfmotorista: params.cpf,
    nome: params.motorista,
    cnpj: params.agendamentoData.cnpj,
    placa: params.placa,
    placa2: params.placa2,
    volume: params.agendamentoData.volume,
    volume2: params.agendamentoData.volume2,
    volumetotal: params.agendamentoData.volumetotal,
    volumeordem: params.agendamentoData.volumeordem,
    status: params.agendamentoData.status,
    placacavalo: params.agendamentoData.placacavalo,
    fa: params.agendamentoData.fa,
    pedido2: params.agendamentoData.pedido2,
    transportadora: params.transportadoraData.fantasia,
    tipoProduto: getTipoProduto(params.produto),
  };

  useEffect(() => {
    imagesRef.current = images;
  }, [images]);

  useEffect(() => {
    return () => {
      imagesRef.current.forEach(image => {
        URL.revokeObjectURL(image.previewUrl);
      });
    };
  }, []);

  useEffect(() => {
    const handleAfterPrint = () => {
      setLoadingPost(false);
      navigate(-1);
    };

    window.addEventListener('afterprint', handleAfterPrint);

    return () => {
      window.removeEventListener('afterprint', handleAfterPrint);
    };
  }, []);

  useEffect(() => {
    async function loadChecklist(): Promise<void> {
      if (paramsToAgendamentoData.agendamentoid && checklist === undefined) {
        setLoading(true);
        await api
          .get(
            `/checklist/etanol/byagendamentoid/${Number(
              paramsToAgendamentoData.agendamentoid,
            )}`,
          )
          .then(response => {
            if (response.data === null) {
              setChecklist([]);
              setLoading(false);
              return;
            }

            setChecklist(response.data);
            setLoading(false);
          })
          .catch(error => {
            console.log('error loading checklist etanol', error);
          });
      }
    }
    loadChecklist();
  }, [paramsToAgendamentoData]);

  const {
    register,
    handleSubmit,
    control,
    setValue,
    formState: { errors },
  } = useForm<FormDataEtanol>();

  useEffect(() => {
    if (params.agendamentoData) {
      (
        Object.keys(
          paramsToAgendamentoData,
        ) as (keyof IAgendamentoEtanolChecklist)[]
      ).forEach(key => {
        setValue(key as keyof FormDataEtanol, paramsToAgendamentoData[key]);
      });
    }
  }, [params.agendamentoData, setValue]);

  const createReportData = (data: FormDataEtanol) => ({
    ...data,
    images: toChecklistReportImages(images),
  });

  const onSubmit: SubmitHandler<FormDataEtanol> = async (
    data: FormDataEtanol,
  ) => {
    try {
      if (data.tipocarreta === '') {
        addToast({
          type: 'error',
          title: 'Checklist',
          description: 'Tipo carreta é obrigatório',
        });
        return;
      }

      if (
        data.volumeordem === undefined ||
        data.volumeordem === null ||
        data.volumeordem === ''
      ) {
        addToast({
          type: 'error',
          title: 'Checklist',
          description: 'Volume Ordem é obrigatório',
        });
        return;
      }
      const formattedDate = moment().format('YYYY-MM-DD HH:mm:ss');

      const formattedData = {
        ...data,
        data: formattedDate,
        volumeordem: Number(data.volumeordem),
      };

      setReportData(createReportData(data));

      // Update Checklist
      if (
        checklist &&
        Number(checklist.agendamentoid) === Number(data.agendamentoid)
      ) {
        setLoadingPost(true);

        await api
          .put(`/checklist/etanol/${checklist.agendamentoid}`, formattedData)

          .then(() => {
            addToast({
              type: 'success',
              title: 'Checklist',
              description: 'Checklist atualizado com sucesso',
              timeout: 1500,
            });

            setLoadingPost(false);
            return;
          })
          .catch(error => {
            console.log('error atualizar checklist', error);
            addToast({
              type: 'error',
              title: 'Checklist',
              description: 'Erro ao atualizar checklist',
            });
            setLoadingPost(false);
          });
      } else {
        // Create Checklist
        setLoadingPost(true);
        await api
          .post('/checklist/etanol', formattedData)
          .then(() => {
            addToast({
              type: 'success',
              title: 'Checklist',
              description: 'Checklist salvo com sucesso',
              timeout: 1500,
            });

            setLoadingPost(false);
            return;
          })
          .catch(error => {
            console.log('error salvar checklist-', error);

            addToast({
              type: 'error',
              title: 'Checklist',
              description: 'Erro ao salvar checklist',
            });
            setLoadingPost(false);
          });
      }

      // Approve checklist
      if (shouldApproveEtanolChecklist(data)) {
        setLoadingPost(true);
        const formattedDate = moment(data.data).format('YYYY-MM-DD');
        const dataWithChecklistApproved = {
          ...data,
          data: formattedDate,
          checklistaprovado: true,
          volumeordem: Number(data.volumeordem),
        };

        api.put(
          `/checklist/etanol/${checklist.agendamentoid}`,
          dataWithChecklistApproved,
        );

        await api
          .post('/fila/checklist', {
            data: data.data,
            cpf: data.cpfmotorista,
            pedido: Number(data.pedido),
            volumeordem: Number(data.volumeordem),
            tipocarreta: TipoCarretaValueEnum[data.tipocarreta], //data.tipocarreta,
            dnit: data.dnit,
            lacres: data.lacres,
            agendamentoid: data.agendamentoid,
            printerName: data.printer,
          })
          .then(async () => {
            setReportData(createReportData(data));

            // addToast({
            //   type: 'success',
            //   title: 'Checklist',
            //   description: 'Checklist aprovado com sucesso',
            //   timeout: 1000,
            // });

            // setTimeout(() => {
            //   window.print();
            // }, 3000);

            const printableReportData = createReportData(data);

            setReportData(printableReportData);

            await waitForImagesToLoad(
              printableReportData.images.map(image => image.url),
            );

            // Allows React to render the report before opening the print dialog.
            requestAnimationFrame(() => {
              requestAnimationFrame(() => {
                window.print();
              });
            });

            clearImages();
          })
          .catch(error => {
            console.log('error aprova checklist-', error.response.data.message);

            addToast({
              type: 'error',
              title: 'Checklist erro',
              description: `Erro ao aprovar checklist, informar ao TI - ${error.response.data.message}`,
            });

            setLoadingPost(false);

            setTimeout(() => {
              navigate(-1);
            }, 3000);
          });
      } else {
        setTimeout(() => {
          navigate(-1);
        }, 3000);
      }
    } catch (err: any) {
      // Close the modal

      console.log('error checklist, err');
      // Show Toast for failure
      addToast({
        type: 'error',
        title: 'Erro atualizar/criar checklist.',
        description: `${err.response.data.message}`,
      });
      setLoadingPost(false);
    }
  };

  return (
    <>
      <Container>
        <header>
          <h1>Bom Sucesso Agroindústria S.A</h1>

          <nav>
            <button type="submit" onClick={() => navigate('/')}>
              Página Inicial
            </button>

            <button type="submit" onClick={() => navigate(-1)}>
              Voltar
            </button>

            <button type="submit" onClick={signOut}>
              Logout
            </button>
          </nav>
        </header>

        <h1
          style={{
            textAlign: 'center',
            color: theme.palette.primary.main,
            marginTop: '40px',
          }}
        >
          Checklist Etanol
        </h1>

        <div
          style={{
            borderBottom: `2px solid ${theme.palette.neutral.midGrey}`,
            height: '1px',
            width: '600px',
            justifySelf: 'center',
            marginTop: '16px',
          }}
        ></div>

        <ContentWrapper>
          {loading && <CircularProgress sx={{ color: '#00a859' }} />}
          <ChecklistWrapper>
            <form
              onSubmit={handleSubmit(onSubmit)}
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
              }}
            >
              {/* 1 - Agendamento */}
              <SectionWrapper>
                <SectionTitle>Agendamento</SectionTitle>
                <GridContainer size={420} columns={3}>
                  <MuiInput
                    name="agendamentoid"
                    label="Agendamento ID"
                    control={control}
                    defaultValue={params.agendamentoid}
                    disabled
                    minWidth={minWidthMuiInput}
                    maxWidth={maxWidthMuiInput}
                  />

                  <MuiInput
                    name="produto"
                    label="Produto"
                    control={control}
                    defaultValue={params.produto}
                    disabled
                    minWidth={minWidthMuiInput}
                    maxWidth={maxWidthMuiInput}
                  />

                  <MuiInput
                    name="spot"
                    label="Spot"
                    control={control}
                    defaultValue={params.spot}
                    disabled
                    minWidth={minWidthMuiInput}
                    maxWidth={maxWidthMuiInput}
                  />

                  <MuiInput
                    name="data"
                    label="Data"
                    control={control}
                    defaultValue={params.data}
                    disabled
                    minWidth={minWidthMuiInput}
                    maxWidth={maxWidthMuiInput}
                  />

                  <MuiInput
                    name="volume"
                    label="Volume"
                    control={control}
                    defaultValue={params.volume}
                    disabled
                    minWidth={minWidthMuiInput}
                    maxWidth={maxWidthMuiInput}
                  />

                  <MuiInput
                    name="volume2"
                    label="Volume 2"
                    control={control}
                    defaultValue={params.agendamentoData?.volume2}
                    disabled
                    minWidth={minWidthMuiInput}
                    maxWidth={maxWidthMuiInput}
                  />

                  <MuiInput
                    name="volumetotal"
                    label="Volume Total"
                    control={control}
                    defaultValue={params.agendamentoData?.volumetotal}
                    disabled
                    minWidth={minWidthMuiInput}
                    maxWidth={maxWidthMuiInput}
                  />

                  <div
                    style={{
                      marginTop: '-16px',
                      height: '56px',
                    }}
                  >
                    <MuiInput
                      rules={{ required: 'Volume obrigatório.' }}
                      name="volumeordem"
                      label="Volume Ordem"
                      control={control}
                      defaultValue={params.agendamentoData?.volumeordem}
                      minWidth={minWidthMuiInput}
                      maxWidth={maxWidthMuiInput}
                    />
                  </div>

                  <div
                    style={{
                      marginTop: '16px',
                      height: '56px',
                    }}
                  >
                    <Select
                      rules={{ required: 'Tipo carreta obrigatório' }}
                      name="tipocarreta"
                      label="Tipo Carreta"
                      control={control}
                      options={TipoCarreta}
                      variant="outlined"
                      minWidth={minWidthMuiInput}
                      maxWidth={maxWidthMuiInput}
                    />
                  </div>
                </GridContainer>
              </SectionWrapper>

              {/* Identificacao */}
              <SectionWrapper>
                <SectionTitle>Identificação</SectionTitle>
                <GridContainer size={420} columns={3}>
                  {ChecklistDataEtanol.Identificacao.map(ident =>
                    ident.type === 'date' ? (
                      <div
                        key={ident.id}
                        style={{
                          width: '400px',
                          marginTop: '16px',
                        }}
                      >
                        <MuiDatePicker
                          key={ident.id}
                          name={ident.name}
                          label={ident.label}
                          control={control}
                          minWidth={minWidthMuiInput}
                          maxWidth={maxWidthMuiInput}
                        />
                      </div>
                    ) : ident.type === 'text' &&
                      ident.name === 'placasTanques' ? (
                      <MuiInput
                        key={ident.name}
                        name={ident.name}
                        label={ident.label}
                        control={control}
                        defaultValue={
                          params.agendamentoData?.placa2
                            ? `${params.agendamentoData?.placa} - ${params.agendamentoData?.placa2}`
                            : params.agendamentoData?.placa
                        }
                        disabled={ident.disabled ? ident.disabled : false}
                        minWidth={minWidthMuiInput}
                        maxWidth={maxWidthMuiInput}
                      />
                    ) : (
                      <MuiInput
                        key={ident.name}
                        name={ident.name}
                        label={ident.label}
                        control={control}
                        defaultValue={
                          params.agendamentoData?.[ident.name] || ''
                        }
                        disabled={ident.disabled ? ident.disabled : false}
                        minWidth={minWidthMuiInput}
                        maxWidth={maxWidthMuiInput}
                      />
                    ),
                  )}
                </GridContainer>
              </SectionWrapper>

              {/* Documentacao */}
              <SectionWrapper>
                <SectionTitle>Documentação</SectionTitle>
                <GridContainer size={420} columns={3}>
                  {ChecklistDataEtanol.Documentacao.map(doc =>
                    doc.type === 'boolean' ? (
                      <div
                        key={doc.id}
                        style={{
                          width: '400px',
                          marginTop: '16px',
                          height: '60px',
                        }}
                      >
                        <MuiCheckbox
                          name={doc.name}
                          label={doc.label}
                          control={control}
                        />
                      </div>
                    ) : doc.type === 'date' ? (
                      <div
                        key={doc.id}
                        style={{
                          width: '400px',
                          marginTop: '16px',
                        }}
                      >
                        <MuiDatePicker
                          key={doc.id}
                          name={doc.name}
                          label={doc.label}
                          control={control}
                          minWidth={minWidthMuiInput}
                          maxWidth={maxWidthMuiInput}
                        />
                      </div>
                    ) : (
                      <MuiInput
                        key={doc.name}
                        name={doc.name}
                        label={doc.label}
                        control={control}
                        defaultValue={params.agendamentoData?.[doc.name] || ''}
                        minWidth={minWidthMuiInput}
                        maxWidth={maxWidthMuiInput}
                      />
                    ),
                  )}
                </GridContainer>
              </SectionWrapper>

              {/* Motorista */}
              <SectionWrapper>
                <SectionTitle>Motorista</SectionTitle>
                <GridContainer size={420} columns={3}>
                  {ChecklistDataEtanol.Motorista.map(mot => (
                    <MuiCheckbox
                      key={mot.id}
                      name={mot.name}
                      label={mot.label}
                      control={control}
                    />
                  ))}
                </GridContainer>
              </SectionWrapper>

              {/* Veículo */}
              <SectionWrapper>
                <SectionTitle>Veículo</SectionTitle>
                <GridContainer size={420} columns={3} rowgap={'16px'}>
                  {ChecklistDataEtanol.Veiculo.map(veiculo => (
                    <MuiCheckbox
                      key={veiculo.id}
                      name={veiculo.name}
                      label={veiculo.label}
                      control={control}
                    />
                  ))}
                </GridContainer>
              </SectionWrapper>

              {/* Seguranca */}
              <SectionWrapper>
                <SectionTitle>Equipamentos de Segurança</SectionTitle>
                <GridContainer size={420} columns={3} rowgap={'16px'}>
                  {ChecklistDataEtanol.Seguranca.map(seguranca => (
                    <MuiCheckbox
                      key={seguranca.id}
                      name={seguranca.name}
                      label={seguranca.label}
                      control={control}
                    />
                  ))}
                </GridContainer>
              </SectionWrapper>

              {/* Sinalizacao */}
              <SectionWrapper>
                <SectionTitle>Sinalização</SectionTitle>
                <GridContainer size={610} columns={2} columngap="16px">
                  {ChecklistDataEtanol.Sinalizacao.map(sinalizacao => (
                    <div
                      key={`${sinalizacao.id}-${sinalizacao.name}`}
                      style={{
                        border: '1px solid grey',
                        borderRadius: '16px',
                        padding: '16px 8px 8px 8px',
                        gap: '8px',
                        justifyContent: 'center',
                        justifyItems: 'center',
                        alignItems: 'flex-start',
                        height: '400px',
                        width: '100%',
                      }}
                    >
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          width: '400px',
                          gap: '16px',
                        }}
                      >
                        <span
                          style={{
                            display: 'flex',
                            marginBottom: '16px',
                            maxWidth: '400px',
                            justifyContent: 'center',
                            height: '80px',
                          }}
                        >
                          {paramsToAgendamentoData.produto === 8 &&
                          sinalizacao.name === 'panelSeguranca'
                            ? 'Possui painel de segurança 40x30 cm na cor laranja com números 30 – 1201 em preto?'
                            : sinalizacao.label}
                        </span>

                        <div
                          style={{
                            display: 'flex',
                            width: '30%',
                          }}
                        >
                          {sinalizacao.name === 'panelSeguranca' && (
                            <img
                              src={
                                paramsToAgendamentoData.produto === 8
                                  ? sign301201
                                  : sign331170
                              }
                              alt={
                                paramsToAgendamentoData.produto === 8
                                  ? 'Sign301201'
                                  : 'Sign331170'
                              }
                              style={{
                                width: '60px',
                                height: '60px',
                              }}
                            />
                          )}

                          {sinalizacao.name === 'rotuloRisco' && (
                            <img
                              src={liquidoInflamavel}
                              alt="liquidoInflamavel"
                              style={{ width: '72px', height: '72px' }}
                            />
                          )}
                        </div>
                      </div>

                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          width: '100%',
                          rowGap: '8px',
                          alignItems: 'center',
                        }}
                      >
                        {ChecklistDataEtanol.SinalizacaoItems.filter(
                          item => item.id === sinalizacao.id,
                        ).map(item => (
                          <MuiCheckbox
                            key={`${item.id}-${item.name}`}
                            name={item.name}
                            label={item.label}
                            control={control}
                          />
                        ))}
                      </div>
                    </div>
                  ))}
                </GridContainer>
              </SectionWrapper>

              {/* Imagens */}
              <SectionWrapper>
                <SectionTitle>Imagens</SectionTitle>
                <GridContainer size={1236} columns={1} columngap="16px">
                  <ChecklistImageUploader
                    images={images}
                    onChange={setImages}
                    maxImages={10}
                    maxFileSizeMb={10}
                    disabled={loadingPost}
                    onValidationError={message => {
                      addToast({
                        type: 'error',
                        title: 'Imagens',
                        description: message,
                      });
                    }}
                  />
                </GridContainer>
              </SectionWrapper>

              {/* Outros */}
              <SectionWrapper>
                <SectionTitle>Outros</SectionTitle>
                <GridContainer size={420} columns={3}>
                  <MuiInput
                    name="lacres"
                    label="Lacres"
                    control={control}
                    defaultValue={''}
                    minWidth={minWidthMuiInput}
                    maxWidth={maxWidthMuiInput}
                  />

                  <MuiInput
                    name="observacoes"
                    label="Observacoes"
                    control={control}
                    defaultValue={''}
                    minWidth={minWidthMuiInput}
                    maxWidth={maxWidthMuiInput}
                  />

                  <div
                    style={{
                      width: '400px',
                      marginTop: '16px',
                      height: '56px',
                    }}
                  >
                    <Select
                      rules={{ required: 'Impressora obrigatória' }}
                      name="printer"
                      label="Impressora"
                      control={control}
                      options={Printers}
                      variant="outlined"
                      minWidth={minWidthMuiInput}
                      maxWidth={maxWidthMuiInput}
                    />
                  </div>

                  {ChecklistDataEtanol.Outros.map(outros => (
                    <MuiInput
                      key={outros.name}
                      name={outros.name}
                      label={outros.label}
                      control={control}
                      defaultValue={params.agendamentoData?.[outros.name] || ''}
                      minWidth={minWidthMuiInput}
                      maxWidth={maxWidthMuiInput}
                    />
                  ))}
                </GridContainer>
              </SectionWrapper>

              <div style={{ marginTop: '72px', marginBottom: '16px' }}>
                <CustomLoadingButton
                  text="Salvar"
                  icon={<FiCheckSquare size={24} />}
                  loading={loadingPost}
                  type="submit"
                  customStyles={{
                    ':hover': { backgroundColor: '#33bc83' },
                    color: theme.palette.neutral.white,
                    fontWeight: '600',
                    fontSize: '22px',
                    background: theme.palette.primary.main,
                    width: '200px',
                    height: '48px',
                    borderRadius: '16px',
                  }}
                />
              </div>
            </form>
          </ChecklistWrapper>
        </ContentWrapper>
      </Container>

      {reportData && <EtanolChecklistReport reportData={reportData} />}
    </>
  );
});

ChecklistEtanol.displayName = 'ChecklistEtanol';

export default ChecklistEtanol;
